import { useMutation, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import coursesAPIs from '../../api/courses';
import sectionsAPIs from '../../api/section';
import Pagination from '../../components/Pagination';
import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import H3Styled from '../../components/Typography/H3Styled';
import { useAuth } from '../../contexts/Auth';
import { isSegreteriaNazionale } from '../../libs/utils/auth';
import StatisticsCourse from './Tables/StatisticsCourse';
import StatisticsEvent from './Tables/StatisticsEvents';
import statisticsAPIs from '../../api/statisticsAPIs';
import moment from 'moment';
import 'moment/locale/it';
import { useEffect, useMemo } from 'react';
import FiltersBar from '../../components/Filters/FiltersBar';
import { FilterType } from '../../interfaces/filters';
import eventsAPIs from '../../api/events';
import { EventStatus } from '../../interfaces/events';
import { ProductType } from '../../interfaces/orders';
import GeneralStatistics from './GeneralStatistics';
import PrimaryButton from '../../components/Buttons/PrimaryButton';
import { toast } from 'react-toastify';
import { DownloadIcon } from '@heroicons/react/outline';
import { CourseStatus } from '../../interfaces/commons';
import { ComparisonStatistic } from './ComparisonStatistic';
import { useStatisticsYears } from './hooks';
import { getStatisticsSearchQuery } from './searchQuery';
import StatisticsYearRadioGroup from './StatisticsYearRadioGroup';
import YearPresetDateFilter from './YearPresetDateFilter';

moment.locale('it');

const { REACT_APP_DEBOUNCE_DELAY } = process.env;
const delay = Number(REACT_APP_DEBOUNCE_DELAY);

export default function Statistiche() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ token, profile }] = useAuth();

  const coursePagination = {
    pageSize: `${ProductType.COURSE}PageSize`,
    page: `${ProductType.COURSE}Page`,
  };

  const eventPagination = {
    pageSize: `${ProductType.EVENT}PageSize`,
    page: `${ProductType.EVENT}Page`,
  };

  const [debounceSearchParams] = useDebounce(searchParams, delay);

  const years = useStatisticsYears();
  const selectedYear = searchParams.get('year');
  const fallbackStatisticsYear = Number(selectedYear) || years[0];

  const coursesSearchQuery = useMemo(
    () => getStatisticsSearchQuery(debounceSearchParams, ProductType.COURSE),
    [debounceSearchParams]
  );

  const eventsSearchQuery = useMemo(
    () => getStatisticsSearchQuery(debounceSearchParams, ProductType.EVENT),
    [debounceSearchParams]
  );

  const statisticSearchQuery = useMemo(() => {
    return {
      year: searchParams.get('year'),
      date: moment().endOf('day').toISOString(),
    };
  }, [searchParams]);

  useEffect(() => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (!nextSearchParams.get(coursePagination.pageSize)) {
      nextSearchParams.set(coursePagination.pageSize, '10');
    }

    if (!nextSearchParams.get(eventPagination.pageSize)) {
      nextSearchParams.set(eventPagination.pageSize, '10');
    }

    if (!nextSearchParams.get('year')) {
      nextSearchParams.set('year', years[0].toString());
    }

    setSearchParams(nextSearchParams);
  }, []);

  const statisticsQuery = useQuery(
    ['findStatistics', selectedYear],
    () => {
      return statisticsAPIs.calculate({
        token,
        query: statisticSearchQuery,
      });
    },
    {
      enabled: Boolean(selectedYear),
    }
  );

  const courseQuery = useQuery(
    [
      'coursesStatistics',
      [
        debounceSearchParams.get('year'),
        debounceSearchParams.get(`${ProductType.COURSE}Sections`),
        debounceSearchParams.get(coursePagination.page),
        debounceSearchParams.get(coursePagination.pageSize),
        debounceSearchParams.get(`${ProductType.COURSE}Date`),
        debounceSearchParams.get(`${ProductType.COURSE}search`),
        debounceSearchParams.get(`${ProductType.COURSE}status`),
      ],
    ],
    () =>
      coursesAPIs.find({
        query: coursesSearchQuery,
        token,
      }),
    {
      enabled:
        Boolean(debounceSearchParams.get('year')) &&
        Boolean(debounceSearchParams.get(coursePagination.pageSize)),
    }
  );

  const eventsQuery = useQuery(
    [
      'eventsStatistics',
      [
        debounceSearchParams.get('year'),
        debounceSearchParams.get(`${ProductType.EVENT}Sections`),
        debounceSearchParams.get(eventPagination.page),
        debounceSearchParams.get(eventPagination.pageSize),
        debounceSearchParams.get(`${ProductType.EVENT}Date`),
        debounceSearchParams.get(`${ProductType.EVENT}search`),
        debounceSearchParams.get(`${ProductType.EVENT}status`),
      ],
    ],
    () =>
      eventsAPIs.find({
        query: eventsSearchQuery,
        token,
      }),
    {
      enabled:
        Boolean(debounceSearchParams.get('year')) &&
        Boolean(debounceSearchParams.get(eventPagination.pageSize)),
    }
  );

  const { mutate: courseStatsCSVMutation, isLoading: isDownloadingCoursesCSV } =
    useMutation('downloadCoursesCSVStats', coursesAPIs.downloadStatsCSV, {
      onError: () => {
        toast.error('Ooops... Qualcosa è andato storto.');
      },
      onSuccess: () => {
        if (Number(courseQuery.data?.meta?.pagination?.total) >= 30000)
          toast.warning(
            "Documento CSV scaricato con successo.\nL'export csv è stato limitato a 30000 elementi."
          );
        else toast.success('Documento CSV scaricato con successo');
      },
    });

  const { mutate: eventStatsCSVMutation, isLoading: isDownloadingEventsCS } =
    useMutation('downloadEventsCSVStats', eventsAPIs.downloadStatsCSV, {
      onError: () => {
        toast.error('Ooops... Qualcosa è andato storto.');
      },
      onSuccess: () => {
        if (Number(courseQuery.data?.meta?.pagination?.total) >= 30000)
          toast.warning(
            "Documento CSV scaricato con successo.\nL'export csv è stato limitato a 30000 elementi."
          );
        else toast.success('Documento CSV scaricato con successo');
      },
    });

  return (
    <div>
      <TitlePage title='Statistiche' />
      <div className='py-10 px-4 lg:px-10'>
        <H1Styled>Statistiche per corsi ed eventi</H1Styled>
        <p className='text-gray-500'>
          Statistiche con indicatori totali per anno e per sezione
        </p>
      </div>

      <div className='pb-7 lg:pb-12 px-4 lg:px-10 w-full space-y-10'>
        <div className='space-y-5'>
          <FiltersBar
            hideAppliedFilters
            filters={[]}
          >
            <StatisticsYearRadioGroup
              inputName='statistics-year'
              selectedYear={fallbackStatisticsYear}
              years={years}
              onSelect={(year) => {
                const nextSearchParams = new URLSearchParams(
                  searchParams.toString()
                );

                nextSearchParams.set('year', year.toString());
                nextSearchParams.delete('page');
                setSearchParams(nextSearchParams);
              }}
            />
          </FiltersBar>
          <GeneralStatistics statisticsQuery={statisticsQuery} />
          <ComparisonStatistic />
        </div>

        <div className='space-y-5'>
          <H3Styled>
            Preventivo e consuntivo <span className='font-medium'>Corsi</span>
          </H3Styled>
          {isSegreteriaNazionale(profile) && (
            <FiltersBar
              filters={[
                {
                  type: FilterType.DATE,
                  label: 'Data',
                  attribute: `${ProductType.COURSE}Date`,
                },
                {
                  type: FilterType.MULTISELECT,
                  attribute: `${ProductType.COURSE}Sections`,
                  label: 'Sezione',
                  key: 'sectionsListFilters',
                  source: (data: any) =>
                    sectionsAPIs.findOptions({
                      ...data,
                      query: { ...data.query },
                    }),
                  searchForAttributes: ['name'],
                },
                {
                  type: FilterType.MULTISELECT,
                  attribute: `${ProductType.COURSE}status`,
                  label: 'Stato',
                  key: 'courseStatus',
                  source: () => ({
                    data: [
                      {
                        id: CourseStatus.DRAFT,
                        attributes: { name: 'Bozza' },
                      },
                      {
                        id: CourseStatus.TO_APPROVE,
                        attributes: { name: 'In approvazione' },
                      },
                      {
                        id: CourseStatus.NOT_APPROVED,
                        attributes: { name: 'Non approvato' },
                      },
                      {
                        id: CourseStatus.PUBLISHED,
                        attributes: { name: 'Pubblicato' },
                      },
                      {
                        id: CourseStatus.FINAL_BALANCE_DELEGATION,
                        attributes: { name: 'Consuntivato da sezione' },
                      },
                      {
                        id: CourseStatus.FINAL_BALANCE_NATIONAL,
                        attributes: {
                          name: 'Consuntivato da segreteria nazionale',
                        },
                      },
                      {
                        id: CourseStatus.CANCELED,
                        attributes: { name: 'Annullato' },
                      },
                    ],
                  }),
                },
                {
                  type: FilterType.SEARCH_BAR,
                  attribute: `${ProductType.COURSE}search`,
                  label: 'Cerca...',
                },
              ]}
            >
              <YearPresetDateFilter
                attributeName={`${ProductType.COURSE}Date`}
                fallbackYear={fallbackStatisticsYear}
                pageAttribute={coursePagination.page}
                years={years}
              />
            </FiltersBar>
          )}
          <div className='flex justify-end w-full'>
            <PrimaryButton
              textSmall
              disabled={isDownloadingCoursesCSV}
              isLoading={isDownloadingCoursesCSV}
              onClick={() => {
                courseStatsCSVMutation({
                  token,
                  query: coursesSearchQuery,
                  fileName: `Statistiche Corsi ONAV ${new Date().getTime()}`,
                });
              }}
            >
              <span className='text-sm underline flex flex-row'>
                <DownloadIcon className='w-4 h-4 mt-[3px]' /> <p>Scarica CSV</p>
              </span>
            </PrimaryButton>
          </div>
          <StatisticsCourse query={courseQuery} />
          <Pagination
            pagination={courseQuery.data?.meta.pagination}
            pageAttrName={coursePagination.page}
            pageSizeAttrName={coursePagination.pageSize}
          />
        </div>

        <div className='space-y-5'>
          <H3Styled>
            Preventivo e consuntivo <span className='font-medium'>Eventi</span>
          </H3Styled>
          {isSegreteriaNazionale(profile) && (
            <FiltersBar
              filters={[
                {
                  type: FilterType.DATE,
                  label: 'Data',
                  attribute: `${ProductType.EVENT}Date`,
                },
                {
                  type: FilterType.MULTISELECT,
                  attribute: `${ProductType.EVENT}Sections`,
                  label: 'Sezione',
                  key: 'sectionsListFilters',
                  source: (data: any) =>
                    sectionsAPIs.findOptions({
                      ...data,
                      query: { ...data.query },
                    }),
                  searchForAttributes: ['name'],
                },
                {
                  type: FilterType.MULTISELECT,
                  attribute: `${ProductType.EVENT}status`,
                  label: 'Stato',
                  key: 'eventStatus',
                  source: () => ({
                    data: [
                      {
                        id: EventStatus.DRAFT,
                        attributes: { name: 'Bozza' },
                      },
                      {
                        id: EventStatus.PUBLISHED,
                        attributes: { name: 'Pubblicato' },
                      },
                      {
                        id: EventStatus.FINAL_BALANCE_DELEGATION,
                        attributes: { name: 'Consuntivato da sezione' },
                      },
                      {
                        id: EventStatus.FINAL_BALANCE_NATIONAL,
                        attributes: {
                          name: 'Consuntivato da segreteria nazionale',
                        },
                      },
                      {
                        id: EventStatus.CANCELED,
                        attributes: { name: 'Annullato' },
                      },
                    ],
                  }),
                },
                {
                  type: FilterType.SEARCH_BAR,
                  attribute: `${ProductType.EVENT}search`,
                  label: 'Cerca...',
                },
              ]}
            >
              <YearPresetDateFilter
                attributeName={`${ProductType.EVENT}Date`}
                fallbackYear={fallbackStatisticsYear}
                pageAttribute={eventPagination.page}
                years={years}
              />
            </FiltersBar>
          )}
          <div className='flex justify-end w-full'>
            <PrimaryButton
              textSmall
              disabled={isDownloadingEventsCS}
              isLoading={isDownloadingEventsCS}
              onClick={() => {
                eventStatsCSVMutation({
                  token,
                  query: eventsSearchQuery,
                  fileName: `Statistiche Eventi ONAV ${new Date().getTime()}`,
                });
              }}
            >
              <span className='text-sm underline flex flex-row'>
                <DownloadIcon className='w-4 h-4 mt-[3px]' /> <p>Scarica CSV</p>
              </span>
            </PrimaryButton>
          </div>
          <StatisticsEvent query={eventsQuery} />
          <Pagination
            pagination={eventsQuery.data?.meta.pagination}
            pageAttrName={eventPagination.page}
            pageSizeAttrName={eventPagination.pageSize}
          />
        </div>
      </div>
    </div>
  );
}
